import {ref} from 'vue';
import {driver} from 'driver.js';
import 'driver.js/dist/driver.css';
import {send} from '@/composables/apiClient';
import {useAuthStore} from '@/stores/authStore';

// Tour completion is stored per user on the server (as a `product_tours`
// setting) so finishing a tour in one browser keeps it dismissed everywhere,
// including a fresh incognito window. localStorage is kept as a local cache so
// completion takes effect immediately without waiting for the next login to
// refetch settings. The login response delivers the server copy as
// authStore.configs.product_tours.
const STORAGE_KEY = 'product_tours';
const SETTING_KEY = 'product_tours';
const activeTour = ref(null);

function parse(raw) {
    if (!raw) return {};
    try {
        const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return value && typeof value === 'object' ? value : {};
    } catch {
        return {};
    }
}

function getLocalState() {
    return parse(localStorage.getItem(STORAGE_KEY));
}

/**
 * Server copy of the tour state, delivered with the login response and held
 * in authStore.configs. Reading it lazily (inside the call, not at module
 * load) keeps Pinia out of the import-time path.
 */
function getServerState() {
    try {
        return parse(useAuthStore().configs?.[SETTING_KEY]);
    } catch {
        return {};
    }
}

/**
 * Merge the server and local copies. A tour is considered seen if either
 * source has recorded it, so a tour completed on another browser stays
 * dismissed and a tour just completed here doesn't flash again before the
 * server write lands.
 */
function getState() {
    return {...getServerState(), ...getLocalState()};
}

/**
 * Persist the merged state to localStorage, the in-memory auth store, and the
 * server. The server write is fire and forget: a tour should never surface an
 * error to the user if the save fails, it just falls back to the local cache.
 */
function saveState(state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

    try {
        const authStore = useAuthStore();
        if (authStore.configs) {
            authStore.updateAuthConfig({...authStore.configs, [SETTING_KEY]: JSON.stringify(state)});
        }
    } catch {
        // auth store not ready, local cache still holds the state
    }

    try {
        send('post', 'settings', {user: true, parameters: {[SETTING_KEY]: JSON.stringify(state)}})
            .catch(() => {});
    } catch {
        // ignore, local cache still holds the state
    }
}

/**
 * Check if a tour has been completed (on any browser for this user).
 */
export function hasSeen(tourName) {
    return !!getState()[tourName];
}

/**
 * Mark a tour as completed.
 */
export function markSeen(tourName) {
    const state = getState();
    state[tourName] = Date.now();
    saveState(state);
}

/**
 * Reset a specific tour (or all tours).
 */
export function resetTour(tourName) {
    if (tourName) {
        const state = getState();
        delete state[tourName];
        saveState(state);
    } else {
        saveState({});
    }
}

/**
 * Start a product tour with the given steps.
 * Returns a promise that resolves when the tour completes or is dismissed.
 */
export function startTour(tourName, steps, options = {}) {
    if (activeTour.value) return Promise.resolve();

    return new Promise((resolve) => {
        activeTour.value = tourName;

        const driverInstance = driver({
            showProgress: true,
            animate: true,
            overlayColor: 'rgba(0, 0, 0, 0.6)',
            stagePadding: 8,
            stageRadius: 8,
            popoverClass: 'simpligps-tour-popover',
            nextBtnText: 'Next',
            prevBtnText: 'Back',
            doneBtnText: 'Got it!',
            ...options,
            steps,
            onDestroyStarted: () => {
                markSeen(tourName);
                activeTour.value = null;
                driverInstance.destroy();
                resolve();
            },
        });

        driverInstance.drive();
    });
}

export function useProductTour() {
    return {activeTour, hasSeen, markSeen, resetTour, startTour};
}
